import { TemplateParseResult } from '@angular/compiler';
import { Injectable } from '@angular/core';

import { ActivatedRoute, ActivatedRouteSnapshot, ParamMap, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { NEVER, Observable, of } from 'rxjs';

import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { LoginRedirectPage } from '../components/login-wizard/models/login-redirect-page.enum';
import { AuthPage } from '../models/auth/auth-page.model';
import { PreferredUserAccessInfo } from '../models/auth/preferred-user-access-info.model';
import { AuthService } from '../services/auth.service';
import { DeficienciesService } from '../services/sections/deficiencies/deficiencies.service';
import { MyWorkService } from '../services/sections/job/my-work.service';
import { UserService } from '../services/sections/shared/user.service';
import { SiteNotesService } from '../services/sections/site-notes/site-notes.service';
import { AccountsService } from '../services/ui/accounts.service';
import { GlobalSettingsService } from '../services/ui/global-settings.service';

interface ErrorType {
	type: string;
}

@Injectable({
	providedIn: 'root'
})
export class TokenResolver implements Resolve<Observable<string>> {
	constructor(
		private router: Router,
		private authService: AuthService,
		private myWorkService: MyWorkService,
		private siteNotesService: SiteNotesService,
		private deficienciesService: DeficienciesService,
		private accountsService: AccountsService,
		private userService: UserService,
		private activatedRoute: ActivatedRoute,
	) { }

	private static hasRedirectedAlready = false;

	resolve(activatedRouteSnapshot: ActivatedRouteSnapshot, routerStateSnapshot: RouterStateSnapshot) {

		GlobalSettingsService.isSecondaryLoaderLoading = true;

		const token = activatedRouteSnapshot.paramMap.get('token');
		const queryParamsMap = activatedRouteSnapshot.queryParamMap;
		const queryParams: { [key: string]: string } = {};
		queryParamsMap.keys.forEach(key => queryParams[key] = queryParamsMap.get(key));

		const getRedirectUri$ = this.userService.getRedirectUri(token)
			.pipe(
				catchError((err) => {
					TokenResolver.hasRedirectedAlready = false;
					if (err.status === 410) {
						const errorType = err?.error as ErrorType;
						if (errorType.type === 'VirtualBinderViewer') {
							this.router.navigate(['/binder-expired']);
							return NEVER;
						}

						if (errorType.type === 'ProcedureCopilotConfirmation') {
							this.router.navigate(['/', token, 'procedure-confirmation-expired']);
							return NEVER;
						}

						if (errorType.type === 'ProcedureSummaryReview') {
							this.router.navigate(['/', token, 'procedure-summary-review-expired']);
							return NEVER;
						}
					}
					
					if (err.status === 403) {
						const errorType = err?.error as ErrorType;
						if (errorType.type === 'VirtualBinderViewer') {
							this.router.navigate(['/binder-disabled']);
							return NEVER;
						}
					}

					this.router.navigate(['/404']);
					return NEVER;
				}),
				map((result: { uri: string }) => {
					const routerBaseUri = routerStateSnapshot.url.split('?')[0];
					if (routerBaseUri !== `/${result.uri}` && routerBaseUri.indexOf(`/${result.uri}/`) !== 0) {
						if (!TokenResolver.hasRedirectedAlready) {
							this.router.navigate([`/${result.uri}`], { queryParams });
							TokenResolver.hasRedirectedAlready = true;
						} else {
							this.router.navigate(['/404']);
							TokenResolver.hasRedirectedAlready = false;
						}
						return null;
					} else {
						TokenResolver.hasRedirectedAlready = false;
					}

					return result;
				}),
				mergeMap((result: { uri: string }) => {
					if (!!result) {
						return of(result.uri);
					}
					return NEVER;
				}),
				mergeMap((uri: string) => {
					const splitUri = uri.split('/');
					const currentPageUrl = splitUri[splitUri.length - 1];

					let request = of({ isAuthorized: true });
					let redirectPage = LoginRedirectPage.MyWork;

					switch (currentPageUrl) {
						case AuthPage.MyWork: request = this.myWorkService.isUserAccountAllowedAccess(activatedRouteSnapshot.paramMap.get('token')); redirectPage = LoginRedirectPage.MyWork; break;
						case AuthPage.Deficiencies: request = this.deficienciesService.isUserAccountAllowedAccess(activatedRouteSnapshot.paramMap.get('token')); redirectPage = LoginRedirectPage.Deficiencies; break;
						default: request = of({ isAuthorized: true });
					}

					return request
						.pipe(
							mergeMap((userAuthorizationInfo: PreferredUserAccessInfo) => {
								if (!!userAuthorizationInfo?.isAuthorized) {
									return of(uri);
								} else {
									this.accountsService.redirectToUserAccountPage(
										redirectPage,
										{
											isAutoAccountRedirect: true,
											preferredAccountId: userAuthorizationInfo.preferredAccountId,
											preferredPlatformTypeId: userAuthorizationInfo.preferredPlatformTypeId,
											preferredEmail: userAuthorizationInfo.email
										}).subscribe();
									return NEVER;
								}
							})
						);
				})
			);

		return getRedirectUri$;
	}
}
